import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../utils/cn';
import EllipseTooltip from '../../../utils/EllipseTooltip';
import { getMaxVisibleFieldTags, getSortedFieldEntries } from '../utils/fieldEntries';

const MAX_FIELD_TAG_ROWS = 2;
const TAG_ROW_CLASS = 'flex w-full min-w-0 flex-wrap justify-center gap-1.5';

function FieldTag({ label, measure, overflow, tooltip }) {
  if (overflow) {
    return (
      <span
        className='inline-flex h-6 shrink-0 items-center justify-center rounded px-2 text-[0.8125rem] font-medium bg-[#E4E6EB] dark:bg-[#2E3440] text-[#454756] dark:text-[#FAFAFA]'
        data-overflow-chip={measure || undefined}
        title={tooltip}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className='inline-block h-6 max-w-full min-w-0 rounded px-2 bg-[#E8E8EA] dark:bg-[#2E3440] text-[#454756] dark:text-[#FAFAFA]'
      data-field-tag={measure || undefined}
    >
      <EllipseTooltip showTooltip={!measure} className='text-[0.8125rem] font-medium leading-6 dark:text-[#FAFAFA]'>
        {label}
      </EllipseTooltip>
    </span>
  );
}

export default function IngredientFieldTags({ fields, noFieldsLabel, className }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(null);

  const entries = useMemo(() => getSortedFieldEntries(fields), [fields]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const update = () => setVisibleCount(getMaxVisibleFieldTags(measure, entries.length, MAX_FIELD_TAG_ROWS));
    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [entries]);

  if (!entries.length) {
    return (
      <div className={cn('w-full min-w-0 flex justify-center', className)}>
        <FieldTag label={noFieldsLabel} />
      </div>
    );
  }

  const visible = visibleCount ?? entries.length;
  const overflow = entries.length - visible;

  return (
    <div ref={containerRef} className={cn('relative w-full min-w-0', className)}>
      <div
        ref={measureRef}
        className={cn(TAG_ROW_CLASS, 'absolute inset-x-0 top-0 opacity-0 pointer-events-none -z-10')}
        aria-hidden
      >
        {entries.map((entry) => (
          <FieldTag key={entry.key} label={entry.label} measure />
        ))}
        <FieldTag label={`+${entries.length}`} measure overflow />
      </div>

      <div className={cn(TAG_ROW_CLASS, 'overflow-hidden max-h-[3.375rem]')}>
        {entries.slice(0, visible).map((entry) => (
          <FieldTag key={entry.key} label={entry.label} />
        ))}
        {overflow > 0 && (
          <FieldTag
            label={`+${overflow}`}
            overflow
            tooltip={entries
              .slice(visible)
              .map((entry) => entry.label)
              .join('\n')}
          />
        )}
      </div>
    </div>
  );
}
